/**
 * Helpers for the operator's default tax rate (Standard-Umsatzsteuersatz).
 *
 * The backend stores the tax rate as a fraction (0.19 = 19 %), the forms work
 * with percent values (0–100, max. 2 decimals, comma or dot as separator).
 * Percent values are converted via integer hundredths of a percent, so no
 * floating point artefacts (e.g. 0.07 * 100 = 7.000000000000001) end up in
 * the form or in the request payload.
 */

export const DEFAULT_TAX_RATE_FRACTION = 0.19;
export const DEFAULT_TAX_RATE_PERCENT = 19;

export const TAX_RATE_PERCENT_ERRORS = {
  REQUIRED: 'required',
  INVALID: 'invalid',
  RANGE: 'range',
  DECIMALS: 'decimals',
};

// i18n keys of the validation messages, per error code
export const TAX_RATE_PERCENT_ERROR_MESSAGE_KEYS = {
  [TAX_RATE_PERCENT_ERRORS.REQUIRED]: 'reqField',
  [TAX_RATE_PERCENT_ERRORS.INVALID]: 'LBLTaxRateInvalid',
  [TAX_RATE_PERCENT_ERRORS.RANGE]: 'LBLTaxRateRange',
  [TAX_RATE_PERCENT_ERRORS.DECIMALS]: 'LBLTaxRateMaxDecimals',
};

const MAX_TAX_RATE_DECIMALS = 2;
const MAX_TAX_RATE_HUNDREDTHS = 100 * 100;
// optional minus (only to report it as out of range), digits, optional decimals after comma or dot
const PERCENT_PATTERN = /^(-)?(\d+)(?:[.,](\d+))?$/;

/**
 * @param {number | string} value - percent value, e.g. 19, '19,5' or '7.25'
 * @returns {{ hundredths?: number, error?: string }} - the value in integer hundredths of a percent or an error code
 */
const analyseTaxRatePercent = (value) => {
  if (value === null || value === undefined) {
    return { error: TAX_RATE_PERCENT_ERRORS.REQUIRED };
  }
  const text = String(value).trim();
  if (text === '') {
    return { error: TAX_RATE_PERCENT_ERRORS.REQUIRED };
  }
  const match = PERCENT_PATTERN.exec(text);
  if (!match) {
    return { error: TAX_RATE_PERCENT_ERRORS.INVALID };
  }
  const [, minus, integerDigits, decimalDigits = ''] = match;
  if (decimalDigits.length > MAX_TAX_RATE_DECIMALS) {
    return { error: TAX_RATE_PERCENT_ERRORS.DECIMALS };
  }
  const hundredths =
    Number(integerDigits) * 100 +
    Number(decimalDigits.padEnd(MAX_TAX_RATE_DECIMALS, '0'));
  if (minus || hundredths > MAX_TAX_RATE_HUNDREDTHS) {
    return { error: TAX_RATE_PERCENT_ERRORS.RANGE };
  }
  return { hundredths };
};

/**
 * @param {number | string} value - percent value, e.g. 19, '19,5' or '7.25'
 * @returns {string | null} - one of TAX_RATE_PERCENT_ERRORS or null if the value is valid
 */
export const getTaxRatePercentError = (value) =>
  analyseTaxRatePercent(value).error || null;

/**
 * @param {number | string} value - percent value, e.g. 19, '19,5' or '7.25'
 * @returns {boolean} - true for 0–100 with max. 2 decimals
 */
export const isValidTaxRatePercent = (value) =>
  getTaxRatePercentError(value) === null;

/**
 * @param {number | string} value - percent value, e.g. 19, '19,5' or '7.25'
 * @returns {number | null} - the percent as number (e.g. 19.5) or null if invalid
 */
export const parseTaxRatePercent = (value) => {
  const { hundredths } = analyseTaxRatePercent(value);
  return hundredths === undefined ? null : hundredths / 100;
};

/**
 * @param {number | string} value - percent value, e.g. 19, '19,5' or '7.25'
 * @returns {number | null} - the fraction sent to the backend (19 -> 0.19, 7.25 -> 0.0725) or null if invalid
 */
export const taxRatePercentToFraction = (value) => {
  const { hundredths } = analyseTaxRatePercent(value);
  return hundredths === undefined ? null : hundredths / 10000;
};

/**
 * @param {number | string} fraction - fraction as returned by the backend, e.g. 0.19
 * @returns {number | null} - the percent rounded to 2 decimals (0.19 -> 19, 0.07 -> 7) or null if not a number
 */
export const taxRateFractionToPercent = (fraction) => {
  if (fraction === null || fraction === undefined || fraction === '') {
    return null;
  }
  const number = Number(fraction);
  if (!Number.isFinite(number)) {
    return null;
  }
  return Math.round(number * 10000) / 100;
};

/**
 * @param {string} language - i18n language, e.g. 'de' or 'en'
 * @returns {string} - decimal separator of the language (de -> ',', otherwise '.')
 */
export const getDecimalSeparator = (language) =>
  String(language || 'de')
    .toLowerCase()
    .startsWith('de')
    ? ','
    : '.';

/**
 * @param {number} percent - percent value, e.g. 7.25
 * @param {string} language - i18n language, e.g. 'de' or 'en'
 * @returns {string} - the percent without trailing zeros in the language's format (de: '7,25', en: '7.25'), '' if not a number
 */
export const formatTaxRatePercent = (percent, language) => {
  if (percent === null || percent === undefined || percent === '') {
    return '';
  }
  const number = Number(percent);
  if (!Number.isFinite(number)) {
    return '';
  }
  const text = String(Math.round(number * 100) / 100);
  return text.replace('.', getDecimalSeparator(language));
};
